import { useState, useEffect, useCallback } from "react";
import { apiFetch, ApiError } from "@/lib/api-client";
import type { JournalEntrySummary, JournalEntriesResponse } from "../types";

interface UseJournalEntriesParams {
  startDate?: string;
  endDate?: string;
  status?: "DRAFT" | "POSTED" | "REVERSED";
  search?: string;
}

interface UseJournalEntriesResult {
  entries: JournalEntrySummary[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook to fetch journal entries with optional filters.
 */
export function useJournalEntries(
  params: UseJournalEntriesParams = {}
): UseJournalEntriesResult {
  const [entries, setEntries] = useState<JournalEntrySummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [fetchKey, setFetchKey] = useState(0);

  useEffect(() => {
    const fetchEntries = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Build query string
        const queryParams = new URLSearchParams();
        if (params.startDate) queryParams.set("startDate", params.startDate);
        if (params.endDate) queryParams.set("endDate", params.endDate);
        if (params.status) queryParams.set("status", params.status);
        if (params.search) queryParams.set("search", params.search);

        const queryString = queryParams.toString();
        const url = `/api/journal-entries${queryString ? `?${queryString}` : ""}`;

        const data = await apiFetch.get<JournalEntriesResponse>(url);
        setEntries(data.entries);
      } catch (err) {
        if (err instanceof ApiError) {
          setError(err);
        } else {
          setError(err instanceof Error ? err : new Error("Unknown error"));
        }
        setEntries([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEntries();
  }, [fetchKey, params.startDate, params.endDate, params.status, params.search]);

  const refetch = useCallback(() => setFetchKey((k) => k + 1), []);

  return {
    entries,
    isLoading,
    error,
    refetch,
  };
}
